import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import deviceGroupService from "../services/deviceGroupService";
import locationService from "../services/locationService";
import macPackageService from "../services/macPackageService";
import GroupAssignmentsModal from "../components/modals/GroupAssignmentsModal";
import GroupMembersModal from "../components/modals/GroupMembersModal";
import { softwareUpdateVersionLabel } from "../services/softwareUpdateService";

const DeviceGroups = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [packageCountByGroup, setPackageCountByGroup] = useState({});
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: "", location: "" });
  const [editingId, setEditingId] = useState(null);
  const [assignGroup, setAssignGroup] = useState(null);
  const [membersGroup, setMembersGroup] = useState(null);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    loadGroups();
    locationService.getLocations().then(setLocations).catch(() => setLocations([]));
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const [data, pkgs] = await Promise.all([
        deviceGroupService.getDeviceGroups(),
        macPackageService.listPackages().catch(() => []),
      ]);
      setGroups(data);
      const counts = {};
      for (const pkg of Array.isArray(pkgs) ? pkgs : []) {
        for (const g of pkg.groups || []) {
          const id = String(g._id || g);
          counts[id] = (counts[id] || 0) + 1;
        }
      }
      setPackageCountByGroup(counts);
    } catch (err) {
      toast.error("Gruppen konnten nicht geladen werden.");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Name ist erforderlich.");
      return;
    }
    const payload = {
      name: formData.name.trim(),
      location: formData.location || undefined,
    };
    try {
      if (editingId) {
        await deviceGroupService.updateDeviceGroup(editingId, payload);
        toast.success("Gruppe aktualisiert.");
      } else {
        await deviceGroupService.createDeviceGroup(payload);
        toast.success("Gruppe angelegt.");
      }
      resetForm();
      loadGroups();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Fehler");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", location: "" });
    setEditingId(null);
  };

  const handleEdit = (grp) => {
    setFormData({
      name: grp.name,
      location: grp.location?._id || "",
    });
    setEditingId(grp._id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Gruppe wirklich löschen?")) return;
    try {
      await deviceGroupService.deleteDeviceGroup(id);
      toast.success("Gruppe gelöscht.");
      loadGroups();
      if (editingId === id) resetForm();
    } catch (err) {
      toast.error("Gruppe konnte nicht gelöscht werden.");
    }
  };

  if (loading && groups.length === 0) {
    return <p className="text-center my-4">Laden...</p>;
  }

  return (
    <div className="container">
      <h2 className="mb-4">Geräte-Gruppen</h2>
      <p className="text-muted mb-4">
        Gruppen bündeln Profile, DDM-Deklarationen, Apps und eine OS-Update-Vorgabe.
        Die Zielversion setzt du unter <Link to="/software-updates">Software Updates</Link> oder im Dialog Zuweisungen.
        Die Nachtruhe weist du unter <Link to="/night-rest">Nachtruhe</Link> Gruppen oder einzelnen Geräten zu.
      </p>

      <form onSubmit={handleSubmit} className="card mb-4 p-3">
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label">Name *</label>
            <input
              type="text"
              className="form-control"
              placeholder="Schul-iPads Standard"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Location</label>
            <select
              className="form-select"
              value={formData.location}
              onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
            >
              <option value="">—</option>
              {locations.map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.schoolNumber} – {loc.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4 d-flex align-items-end gap-2">
            <button type="submit" className="btn btn-primary">
              {editingId ? "Aktualisieren" : "Hinzufügen"}
            </button>
            {editingId && (
              <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                Abbrechen
              </button>
            )}
          </div>
        </div>
      </form>

      <table className="table align-middle">
        <thead>
          <tr>
            <th>Name</th>
            <th>Location</th>
            <th>OS-Update</th>
            <th>Profile</th>
            <th>Pakete</th>
            <th>DDM</th>
            <th>Apps</th>
            <th>Geräte</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {groups.map((grp) => (
            <tr key={grp._id}>
              <td>{grp.name}</td>
              <td>{grp.location ? `${grp.location.schoolNumber} – ${grp.location.name}` : "—"}</td>
              <td>
                {grp.softwareUpdateIdentifier ? (
                  <Link to="/software-updates">{softwareUpdateVersionLabel(grp.softwareUpdateIdentifier)}</Link>
                ) : (
                  <Link to="/software-updates" className="text-muted">setzen</Link>
                )}
              </td>
              <td>{grp.profiles?.length || 0}</td>
              <td>{packageCountByGroup[String(grp._id)] || 0}</td>
              <td>{grp.declarationCount ?? grp.declarations?.length ?? 0}</td>
              <td>{grp.applications?.length || 0}</td>
              <td>{grp.memberCount ?? 0}</td>
              <td className="text-nowrap">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary me-1"
                  onClick={() => setAssignGroup(grp)}
                >
                  Zuweisungen
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary me-1"
                  onClick={() => setMembersGroup(grp)}
                >
                  Geräte
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary me-1"
                  onClick={() => handleEdit(grp)}
                >
                  Bearbeiten
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDelete(grp._id)}
                >
                  Löschen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {groups.length === 0 && (
        <p className="text-muted">Noch keine Gruppen angelegt.</p>
      )}

      <GroupAssignmentsModal
        visible={Boolean(assignGroup)}
        group={assignGroup}
        onClose={() => setAssignGroup(null)}
        onSaved={loadGroups}
      />
      <GroupMembersModal
        visible={Boolean(membersGroup)}
        group={membersGroup}
        onClose={() => setMembersGroup(null)}
      />
    </div>
  );
};

export default DeviceGroups;
